import { useEffect, useState } from "react";
import { Box } from "@storyofams/react-ui";
import { useInView } from "react-intersection-observer";
import { Accordion } from "~components/common/Accordion";
import { Divider } from "~components/common/Divider";
import { Tabs } from "~components/product/Description/InformationTabs/Tabs";
import { FAQ } from "./FAQ";
import { InfoList } from "./InfoList";
import { IngredientsTab } from "./IngredientsTab";
import { Steps } from "./Steps";
import { ToolsTab } from "./ToolsTab";
import { Usage } from "./Usage";

export const RecipeData = ({ recipe }) => {
  const { ref, inView } = useInView({ rootMargin: "-150px" });
  const { ref: toolsRef, inView: isToolsInView } = useInView({
    rootMargin: "-150px",
  });
  const { ref: instructionsRef, inView: isInstructionsInView } = useInView({
    rootMargin: "-150px",
  });
  const { ref: usageRef, inView: isUsageInView } = useInView({
    rootMargin: "-150px",
  });
  const { ref: faqRef, inView: isFAQInView } = useInView({
    rootMargin: "-150px",
  });
  const [tab, setTab] = useState<string>("Ingrediënten");
  const [accordion, setAccordion] = useState(0);
  useEffect(() => {
    setAccordion(0);
  }, [recipe]);
  useEffect(() => {
    if (inView && !isToolsInView) setTab("Ingrediënten");
    else if (isToolsInView && !inView) setTab("Tools & accessoires");
    else if (isInstructionsInView && !isToolsInView) setTab("Instructies");
    else if (isUsageInView && !isInstructionsInView) setTab("Gebruik");
    else if (isFAQInView && !isUsageInView) setTab("FAQ");
  }, [inView, isToolsInView, isInstructionsInView, isUsageInView, isFAQInView]);
  const instructions = {
    PrepTime: recipe.prep_time,
    TotalTime: recipe.total_time,
    yeld: `${recipe?.makes_quantity} ${recipe?.makes_uom?.text}`,
    recipe_price_uom: recipe?.contents_uom.text,
    PricePer: recipe?.makes_uom?.text,
    Niveau: recipe.difficulty.text,
    Houdbaarheid: recipe.best_before_date,
    component: "recipe_information",
  };
  const tabs: string[] = [
    "Ingrediënten",
    recipe?.tools?.length && "Tools & accessoires",
    "Instructies",
    recipe?.how_to_use && "Gebruik",
    recipe?.recipes_faq?.length && "FAQ",
  ];
  const scrollToTab = (tab) => {
    if (tab === "Ingrediënten") setTab("Ingrediënten");
    const el = document?.getElementById(tab);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };
  const scrollToTop = (i) => {
    const el = document?.getElementById(`${"acc" + i}`);

    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 500);
    }
  };
  return (
    <>
      <Box display={["none", "block"]}>
        <InfoList mb={5} content={instructions} />
        <Box
          position={"sticky"}
          zIndex={10}
          top={120}
          backgroundColor={"white"}
        >
          <Tabs
            display={"none"}
            tabs={tabs
              .filter((item) => item)
              .map((item) => ({ tab: item, label: item }))}
            activeTab={tab}
            onClick={(e) => scrollToTab(e)}
            isRecipe
            pb={0}
          />
          <Box
            width={"100"}
            borderTop={"1px solid grey200"}
            position={"relative"}
            top={"-1px"}
          />
        </Box>

        <Box
          ref={ref}
          id="Ingrediënten"
          css={{
            "scroll-margin-top": "200px",
          }}
        >
          <IngredientsTab products={recipe.products} id={recipe.id} />
        </Box>

        {recipe?.tools && recipe?.tools?.length && (
          <Box
            css={{
              "scroll-margin-top": "170px",
            }}
            ref={toolsRef}
            id="Tools & accessoires"
          >
            <ToolsTab
              content={{
                ...recipe.toolsTable,
                products: recipe.tools,
              }}
            />
          </Box>
        )}

        <Box
          css={{
            "scroll-margin-top": "170px",
          }}
          ref={instructionsRef}
          id="Instructies"
        >
          <Steps steps={recipe.recipes_steps} video={recipe?.video_url} />
        </Box>
        <Box
          css={{
            "scroll-margin-top": "170px",
          }}
          ref={usageRef}
          id="Gebruik"
        >
          <Usage howToUse={recipe.how_to_use.text} />
        </Box>
        <Box
          css={{
            "scroll-margin-top": "170px",
          }}
          ref={faqRef}
          id="FAQ"
        >
          <FAQ faq={recipe.recipes_faq} />
        </Box>
      </Box>
      <Box display={["block", "none"]} mt="20px">
        <Divider />
        <Box
          id={"acc" + 0}
          css={{
            "scroll-margin-top": "50px",
          }}
          display={["block", "none"]}
          onClick={() => {
            if (accordion === 0) setAccordion(null);
            else {
              setAccordion(0);
              scrollToTop(0);
            }
          }}
        >
          <Accordion
            title={"Ingrediënten"}
            isOpen={accordion === 0}
            close={accordion !== 0}
          >
            <IngredientsTab products={recipe.products} id={recipe.id} />
          </Accordion>
        </Box>
        {recipe?.tools && recipe?.tools?.length && (
          <Box
            id={"acc" + 1}
            css={{
              "scroll-margin-top": "50px",
            }}
            onClick={() => {
              if (accordion === 1) setAccordion(null);
              else {
                setAccordion(1);
                scrollToTop(1);
              }
            }}
          >
            <Accordion close={accordion !== 1} title={"Tools & accessoires"}>
              <ToolsTab
                content={{
                  ...recipe.toolsTable,
                  products: recipe.tools,
                }}
              />
            </Accordion>
          </Box>
        )}
        <Box
          id={"acc" + 2}
          css={{
            "scroll-margin-top": "50px",
          }}
          onClick={() => {
            if (accordion === 2) setAccordion(null);
            else {
              setAccordion(2);
              scrollToTop(2);
            }
          }}
        >
          <Accordion close={accordion !== 2} title={"Instructies"}>
            <Steps steps={recipe.recipes_steps} video={recipe?.video_url} />
          </Accordion>
        </Box>

        {!!recipe?.how_to_use && (
          <Box
            id={"acc" + 3}
            css={{
              "scroll-margin-top": "50px",
            }}
            onClick={() => {
              if (accordion === 3) setAccordion(null);
              else {
                setAccordion(3);
                scrollToTop(3);
              }
            }}
          >
            <Accordion close={accordion !== 3} title={"Gebruik"}>
              <Usage howToUse={recipe.how_to_use.text} />
            </Accordion>
          </Box>
        )}
        {!!recipe?.recipes_faq?.length && (
          <Box
            id={"acc" + 4}
            css={{
              "scroll-margin-top": "50px",
            }}
            onClick={() => {
              if (accordion === 4) setAccordion(null);
              else {
                setAccordion(4);
                scrollToTop(4);
              }
            }}
          >
            <Accordion title={"FAQ"} close={accordion !== 4}>
              <FAQ faq={recipe.recipes_faq} />
            </Accordion>
          </Box>
        )}
      </Box>
    </>
  );
};
